import React, { useEffect, useState } from "react";
import i18n from "i18n";
import clsx from "clsx";
import { Grid, Menu, MenuItem, Tooltip, withStyles } from "@material-ui/core";
import FormulaFieldFunctions from "./FormulaFieldFunctions";
import { Error } from "common/icons";
import formulaFx from "common/assets/images/formula-fx-icon.svg";
import equal from "common/assets/images/equal_icon.svg";
import { KeyCode } from "common/enums";
import { normalize, trim } from "utils/StringUtils";
import { WBSPropertiesStore } from "modules/WBS/WBSPropertiesStore";

const styles = {
  container: {
    width: "100%",
    background: "#FFFFFF",
    border: "solid #9E9E9E 1px",
    borderRadius: 6,
    height: 32,
    marginTop: 4,
    display: "flex",
    alignItems: "center",
    padding: 4,

    "&.error": {
      background: "rgba(215, 57, 18, 0.05)",
      border: "1px solid #d73912",
    },
  },
  icon: {
    height: 15,
  },
  editable: {
    display: "inline-flex",
    width: "calc(100% - 30px)",
    justifyContent: "flex-start",
    alignItems: "center",
    flexFlow: "nowrap",
    outline: "0",
    fontSize: 14,

    "& .chip": {
      display: "inline-flex",
      alignItems: "center",
      verticalAlign: "middle",
      justifyContent: "center",
      height: 21,
      padding: 0,
      marginLeft: 2,
      marginRight: 2,
      backgroundColor: "#EEEEEE",
      color: "rgba(0, 0, 0, 0.87)",
      fontSize: "0.8125rem",
      fontFamily: "inherit",
      fontWeight: 300,
      whiteSpace: "nowrap" as any,
      textShadow: "0 0 0 black",
      textDecoration: "none",
      border: "1px solid #BDBDBD",
      borderRadius: "9999px",
      outline: "none",
      cursor: "default",
      boxSizing: "border-box" as any,
      transition:
        "background-color 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",

      "&.selected": {
        backgroundColor: "rgba(171, 194, 0, 0.2)",
        borderColor: "rgb(171, 194, 0)",

        "& .chip-icon": {
          color: "rgb(171, 194, 0)",
        },
      },
      "&.error": {
        backgroundColor: "#FFEBE7",
        borderColor: "#D73912",

        "& .chip-icon": {
          color: "#D73912 !important",
        },
      },
      "& .chip-label": {
        cursor: "inherit",
        display: "flex",
        alignItems: "center",
        userSelect: "none" as any,
        whiteSpace: "nowrap" as any,
        padding: "0 12px",
        caretColor: "transparent",
      },
      "& .chip-icon": {
        display: "inline-block",
        width: 17,
        height: 17,
        margin: "0 4px 0 -8px",
        color: "rgba(0, 0, 0, 0.26)",
        fontSize: 24,
        cursor: "pointer",
        userSelect: "none" as any,
        flexShrink: 0,
        fill: "currentColor",
        transition: "fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
      },
    },
  },
  quantityIconError: {
    position: "absolute" as any,
    color: "#D73912",
    width: 17,
    right: 30,
  },
  menuItem: {
    "& .label": {
      flex: "75%" as any,
    },
    "& .info": {
      flex: "25%" as any,
      textAlign: "right" as any,
    },
  },
};

interface IProps {
  classes: any;
  options: { id: string; label: string; unit: string }[];
  formula: string;
  onSave: (ev: any, newFormula: string, type: string) => void;
}

// Variável usada para atualizar a fórmula quando um colchete é fechado
let updateFormula: boolean = false;
// Variável usada para remover excedente do termo pesquisado
let excludeFromMenuSearchTerm: string | undefined = undefined;
// Variável usada para verificar se o chip está selecionado nos eventos de Keyboard
let chipFocused = false;
let _timeoutId;

const FormulaField = ({
  classes,
  formula = "",
  onSave = () => {},
  options = [],
}: IProps) => {
  const [isValid, setIsValid] = useState(true);
  const [error, setError] = useState("");
  const [anchorEl, setAnchorEl] = useState<any>(null);
  const [menuSearchTerm, setMenuSearchTerm] = useState("");

  const menuIsOpen = Boolean(anchorEl);
  const filteredOptions = menuSearchTerm
    ? options.filter(({ label }) =>
        normalize(label).includes(normalize(menuSearchTerm))
      )
    : [...options];

  useEffect(() => {
    updateFormula = false;
    excludeFromMenuSearchTerm = undefined;
    FormulaFieldFunctions.addListenerToSelectionEvent();
  }, []);

  useEffect(() => {
    updateFormula = false;
    handleMenuClose();
    renderFormula(formula);
  }, [formula]);

  const handleKeyDown = (ev) => {
    const target = ev.target;
    const key = "which" in ev ? ev.which : ev.keyCode;

    chipFocused = FormulaFieldFunctions.focusedNodeIsChip();
    try {
      switch (key) {
        case KeyCode.HOME:
          FormulaFieldFunctions.moveCaretToHome(target);
          chipFocused = false;
          break;
        case KeyCode.ARROW_LEFT:
          FormulaFieldFunctions.moveCaretToLeft();
          chipFocused = false;
          break;
        case KeyCode.ARROW_RIGHT:
          FormulaFieldFunctions.moveCaretToRight();
          chipFocused = false;
          break;
        case KeyCode.END:
          FormulaFieldFunctions.moveCaretToEnd(target);
          chipFocused = false;
          break;
        case KeyCode.ARROW_DOWN:
          if (!chipFocused) openMenu(target);
          break;
        case KeyCode.BRACKET_RIGHT:
          if (!chipFocused) openMenu(target);
          break;
        case KeyCode.BRACKET_LEFT:
          if (!chipFocused) updateFormula = true;
          break;
        case KeyCode.TAB:
          if (!chipFocused) selectMenuFirstItem();
          chipFocused = true;
          break;
        case KeyCode.BACKSPACE:
          chipFocused = FormulaFieldFunctions.removeChipIfSelected(true);
          break;
        case KeyCode.DELETE:
          chipFocused = FormulaFieldFunctions.removeChipIfSelected(false);
          break;
        case KeyCode.ENTER:
          handleSave(ev, "enter");
          chipFocused = true;
          break;
      }
    } finally {
      if (chipFocused) {
        ev.stopPropagation();
        ev.preventDefault();
      }
    }
  };

  const handleInput = (ev) => {
    if (_timeoutId) clearTimeout(_timeoutId);
    const target = ev.target;

    if (menuIsOpen) {
      // Força a atualização do conteúdo dentro da Div quando o
      // usuário digitar ']' com menu aberto
      if (updateFormula) {
        updateFormula = false;
        addInputedMenuItem();
        return;
      }

      searchMenuItems(target);
    } else {
      // Para o recalculo em tempo real os dados são salvos a cada
      // modificação e para evitar maiores problemas com o render
      // é usado um timeout
      _timeoutId = setTimeout(() => handleSave(ev, "input"), 1500);
    }
  };

  const handleSave = (ev, type = "blur") => {
    // Remove se tiver um timeout ativo para evitar duplicidade
    // ao salvar os dados
    if (type !== "input" && _timeoutId) clearTimeout(_timeoutId);

    // Cancela a execução da função, pois a atualização da fórmula não deve
    // ocorrer quando é aberto o menu para seleção de informações globais
    if (menuIsOpen) {
      return;
    }

    const text = FormulaFieldFunctions.concatCurrentFormula();
    validateFormula(text);
    onSave(ev, text, type);
  };

  const handleMenuClose = () => {
    excludeFromMenuSearchTerm = undefined;
    setAnchorEl(null);
    setMenuSearchTerm("");
  };

  /** Atualiza as variáveis para abrir o menu de opções, somente se nenhum chip
   * estiver selecionado */
  const openMenu = (target: HTMLElement) => {
    if (!menuIsOpen && !FormulaFieldFunctions.focusedNodeIsChip()) {
      setAnchorEl(target);
    }
  };

  /** Altera o chip digitado para um elemento Chip validando se o texto do chip está
   * correto. */
  const addInputedMenuItem = () => {
    if (!menuIsOpen) return;

    // Obtém elemento com o chip em texto
    const textNode = FormulaFieldFunctions.findChipInText();
    if (!textNode) return;

    // Extrai a label do chip
    const text = textNode.textContent || "";
    const indexOfBracket = text.indexOf("[");
    const textInputed = text.slice(indexOfBracket, text.indexOf("]") + 1);
    const label = text.slice(indexOfBracket + 1, text.indexOf("]"));

    // Verifica se a label é valida
    const optionSelected = options.find(
      (op) => normalize(op.label) === normalize(label)
    );

    // Criar chip
    FormulaFieldFunctions.addChipAtPosition(
      textNode,
      optionSelected ? optionSelected.label : label,
      indexOfBracket,
      textInputed.length,
      !Boolean(optionSelected)
    );
    // Fecha menu
    handleMenuClose();
  };

  /** Pesquisa nos itens do Menu e atualiza a variável usada para aplicar o filtro
   * nas opções do menu.
   */
  const searchMenuItems = (target: HTMLElement) => {
    if (!menuIsOpen) return;

    const text = target.textContent || "";
    const startIndex = text.indexOf("[") + 1;

    // Salva o excedente para depois remover do termo pesquisado
    if (excludeFromMenuSearchTerm === undefined) {
      if (startIndex > 0) {
        excludeFromMenuSearchTerm = text.slice(startIndex);
      }
    }

    let excludeTerm = excludeFromMenuSearchTerm || "";
    if (startIndex > 0 && text.endsWith(excludeTerm)) {
      const newTerm = text.slice(
        startIndex,
        excludeTerm ? -excludeTerm.length : undefined
      );
      setMenuSearchTerm(trim(newTerm));
    } else {
      handleMenuClose();
    }
  };

  /** Seleciona o primeiro item da lista filtrada do menu. */
  const selectMenuFirstItem = () => {
    const firstOption = filteredOptions[0];
    if (firstOption) {
      selectMenuItem(firstOption.label);
    }
  };

  /** Seleciona o item escolhido e cria o chip no lugar do
   * texto pesquisado.
   */
  const selectMenuItem = (label: string) => {
    if (!label || !menuIsOpen) return;

    let textInputed = `[${menuSearchTerm}`;
    let position = 0;
    let nodeFounded = FormulaFieldFunctions.findChipInText(false);
    // Primeiro procura se o chip foi digitado com [
    if (nodeFounded) {
      const indexOfBracket = (nodeFounded.textContent || "").indexOf("[");
      position = indexOfBracket === -1 ? 0 : indexOfBracket;
    } else {
      // Se não pega o elemento em foco atualmente
      const { textNode, offset } = FormulaFieldFunctions.getSelectedNode();
      nodeFounded = textNode as Text;
      position = offset;
      textInputed = "";
    }

    if (nodeFounded) {
      FormulaFieldFunctions.addChipAtPosition(
        nodeFounded,
        label,
        position,
        textInputed.length
      );
    }
    handleMenuClose();
  };

  /** Executa a validação da fórmula e atualiza as variáveis para exibir a mensagem
   * de erro caso haja.
   */
  const validateFormula = (formula: string) => {
    if (!formula) {
      setIsValid(true);
      return;
    }

    const { isValidFormula, status } = WBSPropertiesStore.evaluateFormula(
      formula,
      true
    );

    setIsValid(isValidFormula);
    setError(
      status === "negative_error"
        ? "O valor da quantidade não pode ser negativo"
        : "Encontramos um erro na formula, verifique se todos os caracteres são válidos e a formula está completa."
    );
  };

  const renderFormula = (formula: string) => {
    try {
      formula = trim(formula);
      // Se as formulas forem iguais nem precisa renderizar de novo.
      const currentFormula = FormulaFieldFunctions.concatCurrentFormula();
      if (formula === currentFormula) {
        return;
      }

      FormulaFieldFunctions.updateInputHTML(formula, options);
      validateFormula(formula);
    } catch (error) {
      console.error("Não foi possível atualizar a fórmula", error);
    }
  };

  return (
    <Grid className={clsx(classes.container, { error: !isValid })}>
      <img className={classes.icon} src={formulaFx} />
      <img className={classes.icon} src={equal} />
      <Grid
        id={"divEditable-Formula"}
        className={classes.editable}
        contentEditable={true}
        onKeyDown={handleKeyDown}
        onKeyPress={(ev) => {
          if (chipFocused) ev.preventDefault();
        }}
        onInput={handleInput}
        onBlur={(ev) => handleSave(ev)}
      ></Grid>
      {!isValid && (
        <Tooltip title={error} aria-label="Error">
          <Error className={classes.quantityIconError} />
        </Tooltip>
      )}
      <Menu
        id="formula-long-menu"
        disableAutoFocus
        disableAutoFocusItem
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        open={menuIsOpen}
        onClose={handleMenuClose}
        PaperProps={{
          style: {
            marginTop: 15,
            width: "calc(100vw - 290px - 75px)",
          },
        }}
      >
        {filteredOptions.map(({ id, label, unit }) => {
          const data = WBSPropertiesStore.WBSProjectDataModel;
          let value = data && data[id] ? data[id].valueOf() : "0";

          if (id === "monthRelease") {
            value = data && data.projectTerm ? data.projectTerm.valueOf() : "0";
            unit = value == 1 ? i18n.date.month : i18n.date.months;
          }

          return (
            <MenuItem
              key={id}
              className={classes.menuItem}
              onClick={() => selectMenuItem(label)}
            >
              <div className="label">{label}</div>
              <div className="info">{`${value} ${unit}`}</div>
            </MenuItem>
          );
        })}
      </Menu>
    </Grid>
  );
};

export default withStyles(styles)(FormulaField);



// WEBPACK FOOTER //
// ./src/common/components/FormulaField/index.tsx